

import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiButton, Box, Button, Divider, ErrorInfo, Grid, SectionHeader } from 'web-library';
import { useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { joiResolver } from '@hookform/resolvers/joi';
import { candidateService } from '../../../../api';
import {
  AddressItem,
  MobileNumberItems,
} from '../../../../api/candidate/profile/@candidate/models';
import ContactDetails from '../../../../components/ContactDetails';
import PersonalDetails from '../../../../components/PersonalDetails';
import { CANDIDATE_KEY_GET_PARTICULAR } from '../../../../api/candidate/profile/@candidate/queries-services/keys';
import { fallbackEmptyString } from '../../../../utils/StringUtils';
import { GenericErrorResponse } from '../../../../api-refactored/typings';
import { CandidateProfileFormData } from '../../../../models';
import resitEditCandidateProfileSchema from '../../../accountManagement/schema/resitEditCandidateProfileSchema';

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  submitForm: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    marginRight: theme.spacing(2),
  },
  formContainer: {
    width: '100%',
    height: 'auto',
  },
  btnContainer: {
    '& .MuiButtonBase-root': {
      [theme.breakpoints.down('md')]: {
        width: '100%',
        marginBottom: theme.spacing(2),
      },
    },
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
}));

export interface ConfirmParticularsProps {
  nricFin: string;
  statutoryName: string;
  hanyuPinyinName: string;
  nationality: string;
  citizenship: string;
  schoolName: string;
  email: string;
  phoneNumber: MobileNumberItems;
  mobileNumber: MobileNumberItems;
  mailingAddress: AddressItem;
  foreignAddress1: string;
  foreignAddress2: string;
  foreignAddress3: string;
}

const ConfirmParticulars = ({
  next,
  previous,
  selectedSubjects,
}: {
  next: () => void;
  previous: () => void;
  selectedSubjects: number[];
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const form = useForm({
    mode: 'onSubmit',
    resolver: editMode ? joiResolver(resitEditCandidateProfileSchema()) : undefined,
  });
  const [contactDetailItem, setContactDetailItem] = useState<ConfirmParticularsProps>();

  const [errorMsg, setErrorMsg] = useState<string>('');

  /** fetch candidate profile details
   * */
  candidateService.profile.candidate.useGetCandidateParticular({
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data) {
        setContactDetailItem({
          citizenship: data.citizenship,
          hanyuPinyinName: data.hanyuPinyinName,
          nationality: data.nationality,
          nricFin: data.nricFin,
          schoolName: data.schoolName,
          statutoryName: data.statutoryName,
          email: data.email,
          phoneNumber: data.phoneNumber,
          mobileNumber: data.mobileNumber,
          mailingAddress: data.mailingAddress,
          foreignAddress1: data.foreignAddress1,
          foreignAddress2: data.foreignAddress2,
          foreignAddress3: data.foreignAddress3,
        });
      }
    },
  });

  /** update query for candidate particular
   * */
  const updateCandidateParticular = candidateService.profile.candidate.useUpdateCandidateParticular(
    () => {
      queryClient.invalidateQueries(CANDIDATE_KEY_GET_PARTICULAR).then();
    },
    (err: AxiosError<GenericErrorResponse>) => setErrorMsg(err?.response?.data.message!)
  );

  /** to check whether user is in edit mode
   * */
  const onEdit = (isEdit: boolean) => {
    setEditMode(isEdit);
  };

  useEffect(() => {
    if (selectedSubjects.length === 0) {
      previous();
    }
  }, [previous, selectedSubjects]);

  /** handle form submit
   * */
  const onContactDetailUpdate = async (formData: CandidateProfileFormData) => {
    setErrorMsg('');
    if (editMode) {
      const mobileNumber: MobileNumberItems | undefined =
        formData.mobileCountryCode && formData.mobileContactNumber
          ? {
            countryCode: {
              codeId: formData.mobileCountryCode.codeId,
              codeValue: formData.mobileCountryCode.codeValue,
              codeValueDescription: formData.mobileCountryCode?.codeValueDescription,
            },
            number: formData.mobileContactNumber,
          }
          : undefined;
      const phoneNumber: MobileNumberItems | undefined =
        formData.telephoneCountryCode && formData.telephoneContactNumber
          ? {
            countryCode: {
              codeId: formData.telephoneCountryCode.codeId,
              codeValue: formData.telephoneCountryCode.codeValue,
              codeValueDescription: formData.telephoneCountryCode?.codeValueDescription,
            },
            number: formData.telephoneContactNumber,
          }
          : undefined;

      const mailingAddress: AddressItem | undefined =
        formData.houseOrBlkNo && formData.streetName && formData.postalCode
          ? {
            houseOrBlockNumber: formData.houseOrBlkNo,
            buildingName: formData.building || '',
            streetName: formData.streetName,
            postalCode: formData.postalCode,
            floorNumber: formData.floor,
            unitNumber: formData.unit,
          }
          : undefined;

      const updateData = {
        ...contactDetailItem,
        email: formData.email,
        mobileNumber,
        phoneNumber,
        mailingAddress,
        foreignAddress1: formData.foreignAddress1,
        foreignAddress2: formData.foreignAddress2,
        foreignAddress3: formData.foreignAddress3,
      };

      const updateResponse = await updateCandidateParticular.mutateAsync(updateData);

      if (updateResponse.status === 200) {
        setEditMode(false);
      }
    } else {
      next();
    }
  };

  return (
    <Box className={classes.container}>
      <SectionHeader title="Confirm Particulars" />

      <Grid container>
        <Grid xs={12}>
          <PersonalDetails
            horizontalRule
            statutoryName={fallbackEmptyString(contactDetailItem?.statutoryName)}
            hanyuPinyinName={fallbackEmptyString(contactDetailItem?.hanyuPinyinName)}
            nricFin={fallbackEmptyString(contactDetailItem?.nricFin)}
            nationality={fallbackEmptyString(contactDetailItem?.nationality)}
            citizenship={fallbackEmptyString(contactDetailItem?.citizenship)}
            schoolName={fallbackEmptyString(contactDetailItem?.schoolName)}
          />
        </Grid>
      </Grid>

      <Divider />

      <form className={classes.formContainer} onSubmit={form.handleSubmit(onContactDetailUpdate)}>
        {contactDetailItem && (
          <ContactDetails
            contactDetails={{
              email: contactDetailItem.email,
              phoneNumber: contactDetailItem.phoneNumber,
              mobileNumber: contactDetailItem.mobileNumber,
              mailingAddress: contactDetailItem.mailingAddress,
              foreignAddress1: contactDetailItem.foreignAddress1,
              foreignAddress2: contactDetailItem.foreignAddress2,
              foreignAddress3: contactDetailItem.foreignAddress3,
            }}
            onEdit={onEdit}
            formHook={form}
            editMode={editMode}
            editable
          />
        )}
        <ErrorInfo label={errorMsg} />

        <Grid container className={classes.actionContainer}>
          <Grid container xs={12} md={10} spacing={2}>
            <Grid xs={12} md={3}>
              <Button
                fullWidth
                className={classes.backButton}
                variant="outlined"
                onClick={() => previous()}
              >
                {t('resit->>RegisterResit.Back')}
              </Button>
            </Grid>
            <Grid xs={12} md={6}>
              <ApiButton
                id={`button-confirmParticulars-${!editMode ? 'confirmParticulars' : 'updateParticulars'
                  }`}
                fullWidth
                status={updateCandidateParticular.status}
                type="submit"
              >
                {!editMode && t('resit->>RegisterResit.Confirm Particulars')}
                {editMode && 'Update Particulars'}
              </ApiButton>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ConfirmParticulars;
